import { HeroInlineLogo } from "@/components/dataTable";
import {
  ABSENCE_STATUS_LABELS,
  formatBudgetDays,
  type AbsenceStatus,
  type EmployeeAbsenceBudget,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import type { HeroEmployeeAbsence } from "@/lib/heroEmployeeHr";

interface AbsenceBudgetOverviewProps {
  year: number;
  budget: EmployeeAbsenceBudget | null;
  appRequests: EmployeeAbsenceRequest[];
  heroAbsences: HeroEmployeeAbsence[];
  heroLinked: boolean;
}

interface VacationRow {
  key: string;
  source: "app" | "hero";
  dateFrom: string;
  dateTo: string;
  totalDays: number;
  status: AbsenceStatus;
  note?: string | null;
}

function formatIsoDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

function buildVacationRows(
  appRequests: EmployeeAbsenceRequest[],
  heroAbsences: HeroEmployeeAbsence[],
): VacationRow[] {
  const rows: VacationRow[] = [];
  for (const req of appRequests) {
    if (req.absence_type !== "urlaub") continue;
    if (req.status === "cancelled" || req.status === "rejected") continue;
    rows.push({
      key: `app-${req.id}`,
      source: "app",
      dateFrom: req.date_from,
      dateTo: req.date_to,
      totalDays: Number(req.total_days) || 0,
      status: req.status,
      note: req.note,
    });
  }
  for (const abs of heroAbsences) {
    if (abs.absence_type !== "urlaub") continue;
    if (abs.status === "rejected" || abs.status === "cancelled") continue;
    rows.push({
      key: `hero-${abs.id ?? `${abs.start}-${abs.end}`}`,
      source: "hero",
      dateFrom: abs.start,
      dateTo: abs.end,
      totalDays: Number(abs.total_days) || 0,
      status: abs.status,
      note: abs.comment,
    });
  }
  return rows.sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
}

const OTHER_TYPES: Array<{ usedKey: keyof EmployeeAbsenceBudget; label: string }> = [
  { usedKey: "used_overtime_reduction_days", label: "Überstundenabbau" },
  { usedKey: "used_sick_days", label: "Krankheit" },
  { usedKey: "used_child_sick_days", label: "Kindkrank" },
  { usedKey: "used_special_leave_days", label: "Sonderurlaub" },
  { usedKey: "used_training_days", label: "Schulung / Weiterbildung" },
  { usedKey: "used_unpaid_days", label: "Unbezahlt" },
];

export function AbsenceBudgetOverview({
  year,
  budget,
  appRequests,
  heroAbsences,
  heroLinked,
}: AbsenceBudgetOverviewProps) {
  const vacationRows = buildVacationRows(appRequests, heroAbsences);
  const otherLines = budget
    ? OTHER_TYPES.map((item) => ({
        label: item.label,
        days: Number(budget[item.usedKey] ?? 0) || 0,
      })).filter((line) => line.days > 0)
    : [];

  const available = budget?.available_vacation_days ?? budget?.remaining_vacation_days ?? 0;
  const pending = budget?.pending_vacation_days ?? 0;
  const claim = budget?.vacation_days ?? 0;
  const carryover = budget?.vacation_carryover_days ?? 0;
  const total = budget?.total_vacation_budget ?? claim + carryover;
  const used = budget?.used_vacation_days ?? 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <h3 className="text-sm font-semibold text-label">Aktuelles Urlaubsbudget ({year})</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-label-secondary">noch verfügbar</dt>
              <dd className="font-semibold tabular-nums text-label">
                {formatBudgetDays(available)} Tage
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-label-secondary">davon bereits beantragt</dt>
              <dd className="tabular-nums text-label">{formatBudgetDays(pending)} Tage</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <h3 className="text-sm font-semibold text-label">Details Urlaubsbudget ({year})</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-label-secondary">Jahresanspruch</dt>
              <dd className="tabular-nums text-label">{formatBudgetDays(claim)} Tage</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-label-secondary">
                Resturlaub Vorjahr
                {budget?.vacation_carryover_manual ? " (manuell)" : " (auto)"}
              </dt>
              <dd className="tabular-nums text-label">{formatBudgetDays(carryover)} Tage</dd>
            </div>
            <div className="flex justify-between gap-3 border-t border-separator/60 pt-2">
              <dt className="text-label-secondary">Gesamtbudget</dt>
              <dd className="font-semibold tabular-nums text-label">
                {formatBudgetDays(total)} Tage
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-label-secondary">genommen/genehmigt</dt>
              <dd className="tabular-nums text-label">{formatBudgetDays(used)} Tage</dd>
            </div>
          </dl>
          {heroLinked && (
            <p className="mt-3 text-[11px] text-label-tertiary">
              Genommen = App + Hero, doppelte Tage nur einmal (ohne WE/Feiertage).
            </p>
          )}
        </div>

        <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
          <h3 className="text-sm font-semibold text-label">Sonstige Abwesenheiten ({year})</h3>
          {otherLines.length === 0 ? (
            <p className="mt-3 text-sm text-label-secondary">Keine sonstigen Abwesenheiten.</p>
          ) : (
            <dl className="mt-3 space-y-2 text-sm">
              {otherLines.map((line) => (
                <div key={line.label} className="flex justify-between gap-3">
                  <dt className="text-label-secondary">{line.label}</dt>
                  <dd className="tabular-nums text-label">{formatBudgetDays(line.days)} Tage</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
        <div className="border-b border-separator px-4 py-3">
          <h3 className="text-sm font-semibold text-label">Urlaub {year}</h3>
          <p className="mt-0.5 text-xs text-label-secondary">
            Perioden aus App und Hero. Überlappende Tage zählen im Kontingent nur einmal.
          </p>
        </div>
        {vacationRows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-label-secondary">Keine Urlaubszeiträume in {year}.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-separator bg-fill-quaternary/40 text-left text-xs uppercase tracking-wide text-label-tertiary">
                <th className="px-4 py-2">Von</th>
                <th className="px-4 py-2">Bis</th>
                <th className="px-4 py-2">Tage</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Quelle</th>
              </tr>
            </thead>
            <tbody>
              {vacationRows.map((row) => (
                <tr key={row.key} className="border-b border-separator/60 last:border-b-0">
                  <td className="px-4 py-2.5 tabular-nums text-label">{formatIsoDe(row.dateFrom)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-label">{formatIsoDe(row.dateTo)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-label">
                    {formatBudgetDays(row.totalDays)}
                  </td>
                  <td className="px-4 py-2.5 text-label-secondary">
                    {ABSENCE_STATUS_LABELS[row.status] ?? row.status}
                  </td>
                  <td className="px-4 py-2.5 text-label-secondary">
                    {row.source === "hero" ? (
                      <span className="inline-flex items-center gap-1.5">
                        <HeroInlineLogo className="h-3.5 w-3.5" />
                        Hero
                      </span>
                    ) : (
                      "App"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
